import { useDraggable } from "@dnd-kit/core";
import React, { useCallback, useMemo, useState } from "react";

import SidebarPlayerCardView from "./SidebarPlayerCardView";
import { RADIUS } from "@/config/radius";

/**
 *
 */
export interface SidebarPlayerCardProps {
  playerId: string;
  playerName: string;
  contractValue: string;
  contractMonths: number | null;
  teamName: string;
  age: number;
  archetype: string;
  gpr: number;
  isManual?: boolean;
  isOnKanbanBoard?: boolean;
  kanbanPhaseName?: string;
  onPress?: () => void;
  onDragStart?: (dragData?: unknown, event?: unknown) => void;
  onDragEnd?: () => void;
  onAutoScroll?: (dragX: number) => void;
  /**
   * Replace the card's default contents (SE-6554).
   *
   * The DRAG is what this component really owns — the dnd-kit draggable id
   * (`sidebar-<playerId>`), the `source: "sidebar"` payload the board's drop
   * handlers match on, and the disable-when-already-placed rule. A surface that
   * wants a different-looking row still wants exactly that drag, so it supplies
   * a body here rather than rebuilding the draggable and risking a second,
   * silently divergent copy of the contract.
   *
   * A supplied body OWNS ITS OWN INSETS: the card drops its default padding so
   * the body can run to the card's edges. Legacy callers pass nothing and are
   * unaffected.
   */
  renderBody?: () => React.ReactNode;
}

const SidebarPlayerCardContainer: React.FC<SidebarPlayerCardProps> = React.memo(
  ({
    playerId,
    playerName,
    contractValue,
    contractMonths,
    teamName,
    age,
    archetype,
    gpr,
    isManual = false,
    isOnKanbanBoard = false,
    kanbanPhaseName,
    onPress,
    // Destructured only to keep them off the rest-spread; nothing calls
    // them. See the props above for why they survive.
    onDragStart: _onDragStart,
    onDragEnd: _onDragEnd,
    onAutoScroll: _onAutoScroll,
    renderBody,
  }) => {
    const [isHovered, setIsHovered] = useState(false);

    // Use the formatted value with currency symbol from formatCurrencyShort
    const simplifiedValue = useMemo(() => {
      // contractValue already includes the proper currency symbol
      // Just pass it through as-is
      return contractValue;
    }, [contractValue]);

    const draggableData = useMemo(
      () => ({
        id: playerId,
        playerName,
        contractValue,
        contractMonths,
        teamName,
        age,
        archetype,
        gpr,
      }),
      [
        playerId,
        playerName,
        contractValue,
        contractMonths,
        teamName,
        age,
        archetype,
        gpr,
      ]
    );

    const draggableStyle = useMemo(() => {
      if (isOnKanbanBoard) {
        // Style for players already on Kanban board - same as normal but with reduced opacity
        return {
          backgroundColor: "transparent", // Keep transparent like regular cards
          opacity: 0.6, // Reduced opacity to indicate non-interactive
          borderRadius: RADIUS.md,
          // No transform conflicts - let @dnd-kit handle all transforms
        };
      }

      // Normal interactive style - no conflicting transforms
      return {
        backgroundColor: isHovered ? "rgba(139, 92, 246, 0.1)" : "transparent",
        borderRadius: RADIUS.md,
        // Add subtle shadow on hover
        boxShadow: isHovered ? "0 4px 12px rgba(139, 92, 246, 0.15)" : "none",
        // Remove conflicting React Native transform arrays - let @dnd-kit handle transforms
        // CSS hover effects will be handled by the View component directly
      };
    }, [isHovered, isOnKanbanBoard]);

    const handlePress = useCallback(() => {
      onPress?.();
    }, [onPress]);

    const handleHoverIn = useCallback(() => {
      setIsHovered(true);
    }, []);

    const handleHoverOut = useCallback(() => {
      setIsHovered(false);
    }, []);

    // @dnd-kit registration, its style, and the outer Box's props — all of which
    // the View used to own (SE-8019).
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
      // Unique id so a sidebar card never collides with a kanban card.
      id: `sidebar-${playerId}`,
      data: {
        ...draggableData,
        // Marks the source for DndContext.
        source: "sidebar",
      },
      disabled: isOnKanbanBoard,
    });

    // Pure visibility, no transforms during drag: the DragOverlay is the only
    // element that moves, so a transform here would give the pointer two things
    // to follow.
    const style = useMemo(
      () => ({
        ...draggableStyle,
        transform:
          !isDragging && !isOnKanbanBoard && isHovered
            ? "scale(1.02) translateY(-2px)"
            : undefined,
        cursor: isOnKanbanBoard
          ? "not-allowed"
          : isDragging
            ? "grabbing"
            : "grab",
        opacity: isDragging
          ? 0
          : (draggableStyle as { opacity?: number }).opacity || 1,
        visibility: (isDragging ? "hidden" : "visible") as "hidden" | "visible",
        // Padding lives in the main style to avoid conflicts. A caller-supplied
        // body owns its own insets, so the default padding stands down for it —
        // otherwise a bordered body would sit inset inside an invisible frame.
        paddingVertical: renderBody ? 0 : 8,
        paddingHorizontal: renderBody ? 0 : 16,
        transition: "all 0.2s ease",
      }),
      [draggableStyle, isDragging, isOnKanbanBoard, isHovered, renderBody]
    );

    // design-system: dynamic-style waiver — data-driven value (dnd-kit drag state
    // style + aria/role drag attributes on the UIBox the View renders)
    const boxProps = {
      ref: setNodeRef,
      style,
      ...(isOnKanbanBoard ? {} : listeners),
      ...(isOnKanbanBoard ? {} : attributes),
      "data-sidebar-card": "true",
      className: "sidebar-player-card",
      testID: "PLAYER_KANBAN.SIDEBAR_PLAYER_CARD",
      onPress: isOnKanbanBoard ? undefined : handlePress,
      onHoverIn: isOnKanbanBoard ? undefined : handleHoverIn,
      onHoverOut: isOnKanbanBoard ? undefined : handleHoverOut,
    };

    return (
      <SidebarPlayerCardView
        playerName={playerName}
        simplifiedValue={simplifiedValue}
        contractMonths={contractMonths}
        teamName={teamName}
        age={age}
        archetype={archetype}
        gpr={gpr}
        isManual={isManual}
        isOnKanbanBoard={isOnKanbanBoard}
        kanbanPhaseName={kanbanPhaseName}
        boxProps={boxProps}
        renderBody={renderBody}
      />
    );
  }
);

SidebarPlayerCardContainer.displayName = "SidebarPlayerCardContainer";

export default SidebarPlayerCardContainer;
